import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { recordingSupported, startSmartDictation } from '../lib/speech.js';

export interface MentionCandidate {
  id: string;
  name: string;
  email: string;
}

/** Jabber itself is always mentionable with @jabber. */
const JABBER: MentionCandidate = { id: 'assistant', name: 'Jabberr', email: 'jabber' };

export interface ComposerProps {
  onSend: (content: string) => void | Promise<void>;
  onStop?: () => void;
  streaming: boolean;
  disabled?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  /** When set, the textarea shows this value on first render and clears when a send happens. */
  seedText?: string;
  /** Project members offered by the @-mention autocomplete (OPEN-23). Omit in My space. */
  mentionCandidates?: MentionCandidate[];
}

interface MentionState {
  /** The text after '@' so far. */
  query: string;
  /** Offset of '@' in the value. */
  start: number;
  index: number;
}

function normalized(s: string): string {
  return s.toLowerCase().replace(/[._-]+/g, ' ');
}

export function Composer({ onSend, onStop, streaming, disabled, placeholder, autoFocus, seedText, mentionCandidates }: ComposerProps) {
  const [value, setValue] = useState(seedText ?? '');
  const seedRef = useRef(seedText);
  const consumed = useRef(false);
  // Apply seedText once when first rendered or when it changes and hasn't been consumed yet.
  useEffect(() => {
    if (!consumed.current && seedText !== undefined && seedText !== value) {
      setValue(seedText);
      seedRef.current = seedText;
    }
  }, [seedText, value]);
  const [mention, setMention] = useState<MentionState | null>(null);
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState('');
  const [micError, setMicError] = useState<string | null>(null);
  const ref = useRef<HTMLTextAreaElement>(null);
  const stopDictationRef = useRef<(() => void) | null>(null);
  const dictationOk = useMemo(() => recordingSupported() && !disabled && !streaming, [disabled, streaming]);

  const candidates = useMemo(() => (mentionCandidates && mentionCandidates.length > 0 ? [JABBER, ...mentionCandidates] : undefined), [mentionCandidates]);

  const items = useMemo(() => {
    if (!mention || !candidates) return [];
    const q = normalized(mention.query);
    const list = candidates.filter((c) => normalized(c.name).startsWith(q) || c.email.toLowerCase().startsWith(q) || c.email.split('@')[0]!.toLowerCase().startsWith(q));
    return list.slice(0, 6);
  }, [mention, candidates]);

  const resize = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(240, el.scrollHeight)}px`;
  }, []);

  useEffect(() => {
    resize();
  }, [value, resize]);

  /** Reads the caret: is an @-token open, and what is its query? */
  const mentionAt = useCallback((v: string): MentionState | null => {
    const el = ref.current;
    if (!el || !candidates) return null;
    const caret = el.selectionStart ?? v.length;
    const before = v.slice(0, caret);
    const m = /(?:^|\s)@([^\s@]*)$/.exec(before);
    if (!m) return null;
    const at = before.lastIndexOf('@');
    return { query: m[1] ?? '', start: at, index: 0 };
  }, [candidates]);

  const applyMention = useCallback(
    (candidate: MentionCandidate) => {
      const el = ref.current;
      if (!el || !mention) return;
      // Multi-word names are fine: the server resolves '@Bob Member' to the full name.
      const token = candidate.id === 'assistant' ? 'jabber' : candidate.name;
      const before = `${value.slice(0, mention.start)}@${token} `;
      const after = value.slice((el.selectionStart ?? value.length));
      const next = `${before}${after}`;
      setValue(next);
      setMention(null);
      requestAnimationFrame(() => {
        el.focus();
        el.setSelectionRange(before.length, before.length);
      });
    },
    [mention, value],
  );

  const submit = useCallback(() => {
    const content = value.trim();
    if (!content || streaming || disabled) return;
    consumed.current = true;
    setValue('');
    setMention(null);
    void onSend(content);
    ref.current?.focus();
  }, [value, streaming, disabled, onSend]);

  const stopDictation = useCallback(() => {
    stopDictationRef.current?.();
    stopDictationRef.current = null;
    setListening(false);
    setInterim('');
  }, []);

  useEffect(() => stopDictation, [stopDictation]);

  const toggleDictation = useCallback(() => {
    if (listening) {
      stopDictation();
      return;
    }
    setMicError(null);
    stopDictationRef.current = startSmartDictation({
      onFinal: (text) => {
        setValue((v) => {
          const join = v.length === 0 || /\s$/.test(v) ? '' : ' ';
          return `${v}${join}${text} `;
        });
        requestAnimationFrame(() => {
          const el = ref.current;
          if (el) el.setSelectionRange(el.value.length, el.value.length);
        });
      },
      onInterim: setInterim,
      onError: (message) => {
        setMicError(message);
        stopDictation();
      },
    });
    setListening(true);
  }, [listening, stopDictation]);

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (mention && items.length > 0) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        setMention((m) => (m ? { ...m, index: (m.index + (e.key === 'ArrowDown' ? 1 : items.length - 1)) % items.length } : m));
        return;
      }
      if (e.key === 'Tab' || (e.key === 'Enter' && !e.shiftKey)) {
        e.preventDefault();
        applyMention(items[mention.index % items.length]!);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setMention(null);
        return;
      }
    }
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <form
      className={`composer ${listening ? 'recording' : ''}`}
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <div className="composer-box">
        {listening || micError ? (
          <div className="dictation-status" aria-live="polite">
            {micError ? (
              <span className="muted small">{micError}</span>
            ) : (
              <>
                <span className="dictation-dot" aria-hidden="true" />
                <span className="small">{interim || 'Listening…'}</span>
                <button type="button" className="link-btn" onClick={stopDictation}>
                  Stop
                </button>
              </>
            )}
          </div>
        ) : null}
        {mention && items.length > 0 ? (
          <ul className="mention-list" role="listbox" aria-label="Mention someone" id="mention-list">
            {items.map((c, i) => (
              <li key={c.id} className={i === mention.index ? 'active' : undefined}>
                <button
                  type="button"
                  role="option"
                  id={`mention-option-${i}`}
                  aria-selected={i === mention.index}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    applyMention(c);
                  }}
                >
                  {c.id === 'assistant' ? 'Jabberr (the AI)' : `${c.name || c.email}`}
                  {c.id === 'assistant' ? '' : ` · ${c.email}`}
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        <textarea
          ref={ref}
          rows={1}
          value={value}
          role="combobox"
          aria-expanded={!!(mention && items.length > 0)}
          aria-controls="mention-list"
          aria-activedescendant={mention && items.length > 0 ? `mention-option-${mention.index % items.length}` : undefined}
          onChange={(e) => {
            setValue(e.target.value);
            setMention(mentionAt(e.target.value));
          }}
          onKeyUp={(e) => {
            if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === ' ') setMention(mentionAt(e.currentTarget.value));
          }}
          onClick={(e) => setMention(mentionAt(e.currentTarget.value))}
          onKeyDown={onKeyDown}
          placeholder={placeholder ?? 'Message Jabberr'}
          aria-label="Message"
          autoFocus={autoFocus}
          disabled={disabled}
        />
        <div className="composer-actions">
          <button type="button" className="icon-btn composer-ghost" disabled title="Attachments arrive in V1.5" aria-label="Attach (V1.5)">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 12.5 12.6 21a5.5 5.5 0 0 1-7.8-7.8L13.4 4.6a3.5 3.5 0 0 1 5 5L9.8 18.2a1.5 1.5 0 0 1-2.1-2.1L15.5 8.3" />
            </svg>
          </button>
          <button
            type="button"
            className={`icon-btn composer-ghost mic-btn ${listening ? 'active' : ''}`}
            disabled={!dictationOk}
            title={dictationOk ? (listening ? 'Stop dictation' : 'Dictate with the microphone') : 'Dictation needs Chrome, Edge or Safari'}
            aria-label={listening ? 'Stop dictation' : 'Dictate with the microphone'}
            aria-pressed={listening}
            onClick={toggleDictation}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="9" y="3" width="6" height="12" rx="3" />
              <path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
            </svg>
          </button>
          {streaming ? (
            <button type="button" className="btn btn-stop btn-icon" onClick={onStop} aria-label="Stop">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="6" y="6" width="12" height="12" rx="1" />
              </svg>
            </button>
          ) : (
            <button type="submit" className="btn btn-primary btn-icon btn-icon-circle composer-send" disabled={disabled || value.trim().length === 0} aria-label="Send">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 19V5M5 12l7-7 7 7" />
              </svg>
            </button>
          )}
        </div>
      </div>
      <p className="composer-hint" aria-hidden="true">Enter to send · Shift+Enter for a new line · @ to mention</p>
    </form>
  );
}
